import { useCallback, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LuBell, LuBellRing, LuLoader2 } from "react-icons/lu";
import { SITELINKS } from "../../app-routes/links";
import useAdminNotifications, { AdminNotification } from "../../hooks/useAdminNotifications";
import useDismiss, { DismissReason } from "../../hooks/useDismiss";
import { NotificationIcon, timeAgo } from "./notificationStyle";

/** Bell: unread badge (refreshed every minute) and the latest alerts; each opens its page */
const NotificationBell = () => {
  const { unread, latest, loading, loadLatest, markRead, markAllRead } = useAdminNotifications();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  const badge = unread > 9 ? "9+" : String(unread);

  const dismiss = useCallback((reason: DismissReason) => {
    setOpen(false);
    if (reason === "escape") buttonRef.current?.focus();
  }, []);
  useDismiss(wrapRef, open, dismiss);

  const toggle = () => {
    if (!open) loadLatest();
    setOpen((o) => !o);
  };

  const openItem = (n: AdminNotification) => {
    if (!n.read) markRead(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
        className={`relative flex h-10 w-10 items-center justify-center rounded-full text-honeConnectGray transition-colors hover:bg-hcBg hover:text-honeConnectBlack focus:outline-none focus-visible:ring-2 focus-visible:ring-honeConnectPurple/40 ${
          open ? "bg-hcBg text-honeConnectBlack" : ""
        }`}
      >
        <LuBell className="h-5 w-5" aria-hidden />
        {unread > 0 && (
          <span className="absolute right-1 top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-honeConnectOrange px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white">
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="fixed inset-x-4 top-[72px] z-30 overflow-hidden rounded-xl border border-hcBorder bg-white shadow-pop sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-96"
        >
          <div className="flex items-center justify-between border-b border-hcBorder px-4 py-3">
            <p className="text-sm font-semibold text-honeConnectBlack">
              Notifications{unread > 0 && <span className="ml-1.5 text-hcMuted">({unread} unread)</span>}
            </p>
            <button
              type="button"
              onClick={markAllRead}
              disabled={!unread}
              className="text-xs font-medium text-honeConnectPurple hover:underline disabled:cursor-not-allowed disabled:text-hcMuted disabled:no-underline"
            >
              Mark all as read
            </button>
          </div>

          {loading && latest.length === 0 ? (
            <div className="flex justify-center py-10">
              <LuLoader2 className="h-5 w-5 animate-spin text-hcMuted" aria-hidden />
            </div>
          ) : latest.length === 0 ? (
            <div className="flex flex-col items-center px-6 py-10 text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-hc_F4EBFA text-honeConnectPurple">
                <LuBellRing className="h-5 w-5" aria-hidden />
              </div>
              <p className="text-sm font-semibold text-honeConnectBlack">You're all caught up</p>
              <p className="mt-1 max-w-[260px] text-xs leading-relaxed text-hcMuted">
                New elite applications, refused payments and changes by other admins will show up here.
              </p>
            </div>
          ) : (
            <ul className="max-h-[60vh] divide-y divide-hcBorder overflow-y-auto">
              {latest.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => openItem(n)}
                    className={`flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-hcBg focus:bg-hcBg focus:outline-none ${
                      n.read ? "" : "bg-hc_F4EBFA/40"
                    }`}
                  >
                    <NotificationIcon type={n.type} />
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm ${n.read ? "text-honeConnectGray" : "font-medium text-honeConnectBlack"}`}>{n.title}</p>
                      {n.body && <p className="mt-0.5 line-clamp-2 text-xs text-hcMuted">{n.body}</p>}
                      <p className="mt-1 text-[11px] text-hcMuted">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read && <span aria-label="Unread" className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-honeConnectPurple" />}
                  </button>
                </li>
              ))}
            </ul>
          )}

          <Link
            to={SITELINKS.NOTIFICATIONS}
            onClick={() => setOpen(false)}
            className="block border-t border-hcBorder px-4 py-2.5 text-center text-sm font-medium text-honeConnectPurple hover:bg-hcBg"
          >
            View all notifications
          </Link>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
